import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Botao } from "./botao";
import { cn } from "./cn";

export type ModoDoDialogo = "confirmacao" | "aviso";
export type TomDoConfirmar = "perigo" | "primario";

export type PropsDoDialogoDeConfirmacao = {
  readonly aberto: boolean;
  readonly titulo: string;
  readonly descricao: string;
  /** confirmacao = cancelar + confirmar; aviso = so um botao (substitui window.alert). */
  readonly modo?: ModoDoDialogo;
  readonly rotuloConfirmar?: string;
  readonly rotuloCancelar?: string;
  readonly tomConfirmar?: TomDoConfirmar;
  readonly carregando?: boolean;
  /**
   * Se definido, o usuario precisa digitar este texto (ex.: CONFIRMAR)
   * para habilitar o botao de confirmacao.
   */
  readonly textoParaConfirmar?: string;
  readonly aoConfirmar: () => void;
  readonly aoCancelar: () => void;
};

/**
 * Dialogo unico do app para confirmacao e aviso.
 * Substitui `window.confirm` e `window.alert`. Nunca use os nativos no front.
 *
 * Sempre em `document.body` com `--rk-z-modal` (acima da sidebar da moldura).
 */
export function DialogoDeConfirmacao({
  aberto,
  titulo,
  descricao,
  modo = "confirmacao",
  rotuloConfirmar,
  rotuloCancelar = "Cancelar",
  tomConfirmar = "perigo",
  carregando = false,
  textoParaConfirmar,
  aoConfirmar,
  aoCancelar,
}: PropsDoDialogoDeConfirmacao) {
  const tituloId = useId();
  const descricaoId = useId();
  const inputId = useId();
  const ehAviso = modo === "aviso";
  const rotuloOk =
    rotuloConfirmar ?? (ehAviso ? "Entendi" : "Confirmar");
  const [digitado, definirDigitado] = useState("");
  const exigeTexto = Boolean(textoParaConfirmar) && !ehAviso;
  const textoConfere =
    !exigeTexto ||
    digitado.trim() === (textoParaConfirmar ?? "").trim();

  useEffect(() => {
    if (!aberto) {
      definirDigitado("");
      return;
    }
    definirDigitado("");
  }, [aberto, textoParaConfirmar]);

  useEffect(() => {
    if (!aberto) return;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === "Escape" && !carregando) {
        if (ehAviso) aoConfirmar();
        else aoCancelar();
      }
    };
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("keydown", tecla);
      document.body.style.overflow = overflowAnterior;
    };
  }, [aberto, carregando, ehAviso, aoCancelar, aoConfirmar]);

  if (!aberto || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: "var(--rk-z-modal)" }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={tituloId}
      aria-describedby={descricaoId}
    >
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 cursor-default bg-overlay backdrop-blur-[6px]"
        disabled={carregando}
        onClick={() => {
          if (carregando) return;
          if (ehAviso) aoConfirmar();
          else aoCancelar();
        }}
      />
      <div
        className={cn(
          "relative z-10 w-full max-w-[400px] rounded-2xl border border-border-subtle",
          "bg-popover p-6 text-popover-foreground shadow-e3 animate-subir",
        )}
      >
        <h2
          id={tituloId}
          className="text-lg font-semibold leading-6 tracking-[-0.01em] text-foreground"
        >
          {titulo}
        </h2>
        <p
          id={descricaoId}
          className="mt-1.5 text-[13px] leading-[19px] text-muted-foreground"
        >
          {descricao}
        </p>
        {exigeTexto && textoParaConfirmar ? (
          <div className="mt-4 space-y-2">
            <label
              htmlFor={inputId}
              className="block text-sm font-medium text-foreground"
            >
              Digite{" "}
              <span className="font-semibold tracking-wide">
                {textoParaConfirmar}
              </span>{" "}
              para confirmar
            </label>
            <input
              id={inputId}
              type="text"
              autoComplete="off"
              autoFocus
              value={digitado}
              disabled={carregando}
              onChange={(evento) => definirDigitado(evento.target.value)}
              onKeyDown={(evento) => {
                if (evento.key === "Enter" && textoConfere && !carregando) {
                  evento.preventDefault();
                  aoConfirmar();
                }
              }}
              className={cn(
                "flex h-10 w-full rounded-lg border border-input bg-card px-3.5 py-2",
                "text-sm text-foreground",
                "placeholder:text-muted-foreground",
                "focus-visible:outline-none focus-visible:border-border-stronger focus-visible:ring-[3px] focus-visible:ring-primary/25",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
              placeholder={textoParaConfirmar}
            />
          </div>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          {ehAviso ? null : (
            <Botao
              variante="secundario"
              tamanho="medio"
              disabled={carregando}
              onClick={aoCancelar}
            >
              {rotuloCancelar}
            </Botao>
          )}
          <Botao
            variante={
              ehAviso
                ? "primario"
                : tomConfirmar === "primario"
                  ? "primario"
                  : "perigo"
            }
            tamanho="medio"
            carregando={carregando}
            disabled={!textoConfere}
            onClick={aoConfirmar}
          >
            {rotuloOk}
          </Botao>
        </div>
      </div>
    </div>,
    document.body,
  );
}
